import { getSupabase, isSupabaseConfigured } from '../lib/supabase';
import { assertOnlineOrThrow } from '../lib/offlineGuard';
import { fetchMyAppUserRow } from './profileApi';
import { getRegionKeyForCityLabel } from '../data/regionGate';
import { catalogRowForPick, LOCATION_COUNTRIES, REGION_CITY_ROWS } from '../data/regionsCatalog';
import { getCityNativeLabel } from '../data/onboardingRegionLabels';
import { cacheGetOrSet, CACHE_TTL } from '../lib/cache';

/**
 * @typedef {object} RegionRow
 * @property {string} id
 * @property {string} region_key
 * @property {string|null} name_ko
 * @property {string} name_en
 * @property {string|null} country_code
 * @property {boolean} is_open
 * @property {number} waitlist_count
 * @property {number} open_threshold
 * @property {string|null} opened_at
 */

/** @returns {Promise<RegionRow[]|null>} */
export async function fetchAllRegions() {
  return cacheGetOrSet('regions_all', CACHE_TTL.REGION_LIST, async () => {
    if (!isSupabaseConfigured) return null;
    const sb = getSupabase();
    if (!sb) return null;
    assertOnlineOrThrow();
    const { data, error } = await sb.from('regions').select('*');
    if (error) return null;
    return data || [];
  });
}

/**
 * @param {string} cityLabel
 * @param {RegionRow[]|null|undefined} rows
 * @returns {RegionRow|undefined}
 */
export function getRegionForCity(cityLabel, rows) {
  if (!rows?.length) return undefined;
  const key = getRegionKeyForCityLabel(cityLabel);
  if (!key) return undefined;
  return rows.find((r) => r.region_key === key);
}

/**
 * @param {string[]} cityLabels
 * @param {RegionRow[]|null|undefined} rows
 * @returns {string[]}
 */
export function getClosedCitiesInPick(cityLabels, rows) {
  if (!rows?.length) return [];
  const out = [];
  for (const c of cityLabels) {
    const r = getRegionForCity(c, rows);
    if (r && !r.is_open) out.push(c);
  }
  return out;
}

/**
 * @param {string} regionKey
 * @param {{ countryCode: string, language: string, email: string | null }} meta
 * @returns {Promise<{ ok: boolean, duplicate?: boolean, error?: import('@supabase/supabase-js').PostgrestError }>}
 */
export async function joinRegionWaitlist(regionKey, { countryCode, language, email }) {
  if (!isSupabaseConfigured) return { ok: false, error: { message: 'NO_SB' } };
  const sb = getSupabase();
  if (!sb) return { ok: false, error: { message: 'NO_SB' } };
  assertOnlineOrThrow();
  const me = await fetchMyAppUserRow();
  if (!me?.id) return { ok: false, error: { message: 'NO_USER' } };
  const { data: auth } = await sb.auth.getUser();
  const mail = email || auth.user?.email || null;
  const { error } = await sb.from('waitlist').insert({
    user_id: me.id,
    region: regionKey,
    country_code: countryCode || null,
    language: language || null,
    email: mail,
  });
  if (error) {
    if (error.code === '23505') {
      return { ok: true, duplicate: true };
    }
    return { ok: false, error };
  }
  return { ok: true };
}

/** Staff-admin only (RLS/RPC checks auth.uid()). @returns {Promise<RegionRow[]|null>} */
export async function adminListRegions() {
  const sb = getSupabase();
  if (!sb) return null;
  const { data, error } = await sb.rpc('admin_list_regions');
  if (error) return null;
  return data;
}

export async function adminOpenRegion(pRegionKey) {
  const sb = getSupabase();
  if (!sb) return { ok: false };
  const { error } = await sb.rpc('admin_open_region', { p_region_key: pRegionKey });
  if (error) return { ok: false, error };
  return { ok: true };
}

export async function adminNotifyRegionWaiters(pRegionKey) {
  const sb = getSupabase();
  if (!sb) return { ok: false, n: 0 };
  const { data, error } = await sb.rpc('admin_notify_region_waiters', {
    p_region_key: pRegionKey,
  });
  if (error) return { ok: false, n: 0, error };
  return { ok: true, n: data ?? 0 };
}

const LEGACY_CITY_COUNTRY = {
  'Ha Noi': 'VN',
  'Hanoi': 'VN',
  'Ho Chi Minh City': 'VN',
  'Ho Chi Minh': 'VN',
  다낭: 'VN',
  호치민: 'VN',
  하노이: 'VN',
  서울: 'KR',
  부산: 'KR',
  광주: 'KR',
};

function normCityLabelForCountry(s) {
  return String(s || '')
    .trim()
    .normalize('NFC')
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

export function getCountryCodeForCity(city) {
  const raw = String(city || '').trim();
  if (!raw) return '';
  if (LEGACY_CITY_COUNTRY[raw]) return LEGACY_CITY_COUNTRY[raw];
  const norm = normCityLabelForCountry(raw);
  const catalog =
    catalogRowForPick(raw) ||
    REGION_CITY_ROWS.find((r) => {
      const names = [r.pick, r.ko, getCityNativeLabel(r)].map(normCityLabelForCountry);
      return names.includes(norm);
    });
  if (catalog?.country) return catalog.country;
  for (const c of LOCATION_COUNTRIES) {
    if (c.cities.includes(raw)) return c.code;
  }
  return '';
}

/** @param {string} countryCode */
export function getSuggestedUiLanguageForCountry(countryCode) {
  const m = {
    VN: 'vi',
    TH: 'th',
    PH: 'en',
    ID: 'id',
    MY: 'en',
    SG: 'en',
    KH: 'en',
    MM: 'en',
    JP: 'ja',
    CN: 'zh-CN',
    TW: 'zh-TW',
    KR: 'ko',
    AU: 'en',
    US: 'en',
    CA: 'en',
    GB: 'en',
    FR: 'en',
    DE: 'en',
    NL: 'en',
  };
  return m[countryCode] || null;
}
